import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit2, Trash2, MapPin } from 'lucide-react';
import { Button } from '@common/Button';
import { collection, query, where, getDocs, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@config/firebase';
import { POI_TYPES } from '@modules/roadscope/constants/poiTypes';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';
import { LoadingSpinner } from '@common/LoadingSpinner';

export const POIsTab = ({ survey }) => {
  const navigate = useNavigate();
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Fetch POIs for this survey
  useEffect(() => {
    if (!survey?.id) return;

    const poisRef = collection(db, 'pois');
    const poisQuery = query(poisRef, where('surveyId', '==', survey.id));

    const unsubscribe = onSnapshot(poisQuery, 
      (snapshot) => {
        const poisData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPois(poisData);
        setLoading(false);
        logInfo(`Loaded ${poisData.length} POIs`);
      },
      (error) => {
        logError('Error loading POIs:', error);
        setLoading(false);
        setToast({
          type: 'error',
          message: 'Failed to load POIs'
        });
      }
    );

    return () => unsubscribe();
  }, [survey?.id]);

  const handleViewPOI = (poi) => {
    navigate('/map', {
      state: {
        surveyId: survey.id,
        poiId: poi.id,
        center: poi.coordinates ? {
          lat: poi.coordinates.latitude,
          lng: poi.coordinates.longitude
        } : null
      }
    });
  };

  const handleEditPOI = (poi) => {
    navigate('/pois', {
      state: {
        surveyId: survey.id,
        selectedPoi: poi.id
      }
    });
  };

  const handleDeletePOI = async (poi) => {
    if (!window.confirm('Are you sure you want to delete this POI?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'pois', poi.id));
      setToast({
        type: 'success',
        message: 'POI deleted successfully'
      });
      logInfo('POI deleted successfully');
    } catch (error) {
      logError('Failed to delete POI:', error);
      setToast({
        type: 'error',
        message: 'Failed to delete POI'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {pois.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No POIs recorded yet
        </div>
      ) : (
        <div className="space-y-4">
          {pois.map((poi) => {
            const poiType = POI_TYPES[poi.type];
            const Icon = poiType?.icon || MapPin;

            return (
              <div 
                key={poi.id}
                className="bg-[#2A2B2E] rounded-lg p-4 border border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${poiType?.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: poiType?.color }} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">
                        {poiType?.prefix || 'POI'}-{poi.id}
                      </h3>
                      <div className="text-sm text-gray-400 mt-1">
                        <div>
                          {poi.coordinates ? 
                            `${Number(poi.coordinates.latitude).toFixed(6)}, ${Number(poi.coordinates.longitude).toFixed(6)}` :
                            'No coordinates'
                          }
                        </div>
                        {Array.isArray(poi.measurements) && poi.measurements.length > 0 && (
                          <div>
                          Measurements: {poi.measurements.map(m => {
                            const value = m?.value ? Number(m.value).toFixed(2) : '0.00';
                            const unit = m?.unit || '';
                            return `${value}${unit}`;
                          }).join(' × ')}
                          </div>
                        )}
                        {poi.notes && (
                          <div>Notes: {poi.notes}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleViewPOI(poi)}
                      className="p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded"
                      title="View on Map"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleEditPOI(poi)}
                      className="p-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded"
                      title="Edit POI"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeletePOI(poi)}
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                      title="Delete POI"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};